import { z } from 'zod';

import { E164_EXAMPLE, E164_RE } from '../../shared/lib/phone';

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const signupSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name is too long'),
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  phone: z.string().trim().regex(E164_RE, `Enter a valid phone number, e.g. ${E164_EXAMPLE}`),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(72, 'Password must be at most 72 characters'),
});

export type SignupInput = z.infer<typeof signupSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

// Same bounds as signup: 8 is the product floor, 72 is bcrypt's — a longer
// password is silently truncated by the hash, so it is rejected instead.
export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(72, 'Password must be at most 72 characters'),
    confirm: z.string().min(1, 'Re-enter your new password'),
  })
  .refine((values) => values.password === values.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// Phase 90 — institute-branded student login (/i/:slug/login). The username is
// mapped to a synthetic email client-side, so only presence is validated here.
export const instituteLoginSchema = z.object({
  username: z.string().trim().min(1, 'Enter your username'),
  password: z.string().min(1, 'Password is required'),
});

export type InstituteLoginInput = z.infer<typeof instituteLoginSchema>;

// Phase 126 — the blocking "complete your profile" dialog. Google sign-in gives
// us a name but never a phone, and older accounts (including every
// institute-provisioned student) were stored with phone = ''. Mirrored
// server-side by complete_profile() so the RPC re-validates rather than trusting
// the client.
export const completeProfileSchema = z.object({
  display_name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long'),
  phone: z.string().trim().regex(E164_RE, `Enter a valid phone number, e.g. ${E164_EXAMPLE}`),
});

export type CompleteProfileInput = z.infer<typeof completeProfileSchema>;

export const verifyOtpSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[0-9]{4,8}$/, 'Enter the numeric code (4–8 digits)'),
});

export type VerifyOtpInput = z.infer<typeof verifyOtpSchema>;
